import {
  faArrowDownToBracket,
  faCheck,
  faChevronDown,
  faChevronUp,
  faEdit,
  faPaperclip,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  ContextMenu,
  ContextMenuItem,
  ContextMenuList,
  ContextMenuTrigger,
  DestructiveModal,
  Flex,
  Heading,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  PlusIcon,
  SquareButton,
  Text,
  TextButton,
  ThreeDotsIcon,
  Toast,
  Tooltip,
} from '@modusign/design-system';
import {
  addAttachmentToDocument,
  downloadAttachment,
  downloadDocumentFile,
  getGetDocumentQueryKey,
  updateAttachment,
  updateDocumentFile,
  useChangeAttachmentToDocumentFile,
  useRemoveAttachment,
} from '@modusign/openapi-client-external-document';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form-v7';
import { useMutation, useQueryClient } from 'react-query';
import { useToggle } from 'react-use';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import {
  FileUploadErrorBuilder,
  IFileUploadError,
} from '@app/components/FileUploadErrorModal/fileUploadErrorBuilder';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { useExternalDocumentPermission } from '@app/features/externalDocument/hooks/useExternalDocumentPermission';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { validateAttachmentFile } from '@app/libs/file/validateFile';
import { downloadFileFromURL } from '@app/store/transport-layer/files';

import FileUploadErrorModal from '../../../../../components/FileUploadErrorModal';
import { MAXIMUM_FILE_LIMIT } from '../../../constants';
import checkIsContractableFileName from '../../../libs/checkIsContractableFileName';
import uploadFile from '../../../libs/uploadFile';
import deduplicateFileNameFactory from '../../UploadPage/deduplicateFileNameFactory';

interface FileListProps {
  documentId: string;
  files: Array<{ id: string; name: string; extension: string; isContract: boolean }>;
  isCompact?: boolean;
  onClick: (params: { id: string; isContract: boolean }) => void;
}

export default function FileList({ documentId, files, isCompact, onClick }: FileListProps) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const { updatePermission } = useExternalDocumentPermission(documentId);

  const invalidate = () =>
    queryClient.invalidateQueries(getGetDocumentQueryKey(workspaceId, documentId));

  const { createErrorHandler } = useOpenErrorModal();
  const {
    mutate: uploadAttachment,
    isLoading: isUploading,
    error,
    reset,
  } = useMutation<void, IFileUploadError[], { files: File[] }>(async ({ files: uploadFiles }) => {
    const acceptedFiles: Array<{ idx: number; file: File }> = [];
    const errors: { idx: number; error: IFileUploadError }[] = [];

    await Promise.all(
      uploadFiles.map(async (file, idx) => {
        const errorBuilder = new FileUploadErrorBuilder(file.name);
        if (files.length + acceptedFiles.length + 1 > MAXIMUM_FILE_LIMIT) {
          errorBuilder.fileNumberExceededError(MAXIMUM_FILE_LIMIT);
        }
        if (file.size > 10 * 1000 * 1000) {
          errorBuilder.fileSizeExceededError(10 * 1000 * 1000);
        }
        const extensionInvalid = !(await validateAttachmentFile(file));
        if (extensionInvalid) {
          errorBuilder.fileExtensionError();
        }

        const error = errorBuilder.build();

        if (error) {
          errors.push({ idx, error });
          return;
        }

        acceptedFiles.push({
          idx,
          file,
        });
      })
    );

    const deduplicateFileName = deduplicateFileNameFactory(files.map(({ name }) => name));
    const promises = acceptedFiles.map(({ idx, file }) => async () => {
      try {
        const key = await uploadFile(file, 'ATTACHMENT');
        return {
          externalDocumentId: documentId,
          name: deduplicateFileName(file.name),
          key,
        };
      } catch (e) {
        const errorBuilder = new FileUploadErrorBuilder(file.name);
        errorBuilder.otherError(e.message);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        errors.push({ idx, error: errorBuilder.build()! });
        return null;
      }
    });

    const PQueue = (await import('p-queue')).default; // eslint-disable-line import/no-unresolved
    const queue = new PQueue({ concurrency: 3 });
    const fileKeys = await queue.addAll(promises);

    // 동시에 여러 문서를 업데이트하는 경우 서버에서 오류가 발생해 파일을 하나씩 추가하도록 합니다.
    for (let i = 0; i < fileKeys.length; i += 1) {
      const fileKey = fileKeys[i];
      if (!fileKey) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const { externalDocumentId, key, name } = fileKey;
      // eslint-disable-next-line no-await-in-loop
      await addAttachmentToDocument(workspaceId, externalDocumentId, { name, key }).catch((e) => {
        const builder = new FileUploadErrorBuilder(name);
        builder.otherError(e.message);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        errors.push({ idx: i, error: builder.build()! });
      });
    }

    invalidate();

    if (errors.length) {
      throw errors.sort((a, b) => (a.idx < b.idx ? -1 : 1)).map(({ error }) => error);
    }
  });

  const { mutate: downloadFile, isLoading: isDownloading } = useMutation(
    async ({
      id,
      isContract,
      name,
      extension,
    }: {
      id: string;
      isContract: boolean;
      name: string;
      extension: string;
    }) => {
      let url: string;
      if (isContract) {
        url = (await downloadDocumentFile(workspaceId, documentId, id)).url;
      } else {
        url = (await downloadAttachment(workspaceId, documentId, id)).url;
      }

      await downloadFileFromURL({ url, saveFileName: `${name}.${extension}` });
    },
    { onError: createErrorHandler({ title: '파일 다운로드 실패' }) }
  );

  const { mutate: deleteFile, isLoading: isDeleting } = useRemoveAttachment({
    mutation: {
      onError: createErrorHandler({ title: '파일 삭제 실패' }),
      onSuccess: () => {
        Toast.success({
          description: `${modalInfo?.name}.${modalInfo?.extension} 파일을 삭제했습니다.`,
        });
        setModalInfo(null);
        invalidate();
      },
    },
  });

  const { mutate: setAttachmentAsContractFile, isLoading: isSettingAttachmentAsContractFile } =
    useChangeAttachmentToDocumentFile({
      mutation: {
        onError: createErrorHandler({ title: '파일 설정 실패' }),
        onSuccess: () => {
          Toast.success({ description: '계약서로 설정이 완료되었습니다.' });
          invalidate();
        },
      },
    });

  const { mutate: changeName, isLoading: isChangingName } = useMutation(
    async ({ id, isContract, name }: { id: string; isContract: boolean; name: string }) => {
      if (isContract) {
        await updateDocumentFile(workspaceId, documentId, id, { name });
      } else {
        await updateAttachment(workspaceId, documentId, id, { name });
      }
    },
    {
      onError: createErrorHandler({ title: '파일 이름 변경 실패' }),
      onSuccess: () => {
        setModalInfo(null);
        invalidate();
      },
    }
  );

  const isLoading = isDownloading || isDeleting || isSettingAttachmentAsContractFile;

  const fileUploadRef = useRef<HTMLInputElement>(null);
  const [expanded, toggleExpand] = useToggle(false);

  const [modalInfo, setModalInfo] = useState<{
    type: 'NAME_CHANGE' | 'DELETE';
    id: string;
    name: string;
    extension: string;
    isContract: boolean;
  } | null>(null);

  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.files?.length) {
      uploadAttachment({ files: Array.from(e.target.files) });
      e.target.value = '';
    }
  };

  return (
    <>
      {isLoading && <GlobalLoadingSpinner />}
      <input
        multiple
        ref={fileUploadRef}
        style={{ display: 'none' }}
        type="file"
        onChange={handleFileUpload}
      />
      <FileUploadErrorModal errors={error} onConfirm={() => reset()} />
      <Flex justifyContent="space-between">
        <Heading as="h2" fontSize="sm" fontWeight="medium">
          <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: '6px' }} />
          파일
        </Heading>
        {updatePermission.isPermitted && (
          <TextButton
            sx={{ display: ['none', null, 'flex'], alignItems: 'center' }}
            onClick={() => fileUploadRef.current?.click()}
          >
            <PlusIcon marginRight="2px" variant="line" />
            추가
          </TextButton>
        )}
      </Flex>
      <Flex as="ul" flexDirection="column" gap={2} mt={7}>
        {files
          .filter((_, i) => (!isCompact || expanded ? true : i === 0))
          .map(({ id, isContract, name, extension }) => (
            <FileListItem
              extension={extension}
              hasUpdatePermission={updatePermission.isPermitted}
              isContract={isContract}
              key={id}
              name={name}
              onClick={() => onClick({ id, isContract })}
              onDeleteClick={() =>
                setModalInfo({ type: 'DELETE', id, name, extension, isContract })
              }
              onDownloadClick={() => downloadFile({ id, isContract, name, extension })}
              onNameChangeClick={() =>
                setModalInfo({ type: 'NAME_CHANGE', id, name, extension, isContract })
              }
              onSetContractClick={() => {
                if (!checkIsContractableFileName(`${name}.${extension}`)) {
                  Toast.error({
                    title: '계약서로 설정할 수 없는 파일입니다.',
                    description: `{.${extension}} 확장자는 계약서로 설정할 수 없습니다.`,
                  });
                  return;
                }
                setAttachmentAsContractFile({
                  workspaceId,
                  externalDocumentId: documentId,
                  data: { attachmentId: id },
                });
              }}
            />
          ))}
        {isUploading && (
          <Box as="li" height="60px" position="relative">
            <CenterLoadingSpinner size={24} />
          </Box>
        )}
      </Flex>
      {isCompact && (
        <TextButton mt={4} onClick={toggleExpand}>
          {expanded ? '간략히 보기' : '파일 더 보기'}{' '}
          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        </TextButton>
      )}
      {modalInfo?.type === 'DELETE' && (
        <DestructiveModal
          confirmButtonLabel="삭제하기"
          isLoading={isDeleting}
          isOpen
          maxWidth="xs"
          title="파일 삭제"
          onClose={() => setModalInfo(null)}
          onConfirm={() => {
            setModalInfo(null);
            deleteFile({ workspaceId, externalDocumentId: documentId, attachmentId: modalInfo.id });
          }}
        >
          <Text textAlign="center">
            {modalInfo.name}.{modalInfo.extension} 파일을 삭제할까요?
          </Text>
        </DestructiveModal>
      )}
      {modalInfo?.type === 'NAME_CHANGE' && (
        <NameChangeModal
          isLoading={isChangingName}
          isOpen
          name={modalInfo.name}
          onCancel={() => setModalInfo(null)}
          onConfirm={(name) => {
            changeName({ id: modalInfo.id, isContract: modalInfo.isContract, name });
            setModalInfo(null);
          }}
        />
      )}
    </>
  );
}

interface FileListItemProps {
  name: string;
  extension: string;
  isContract: boolean;
  hasUpdatePermission: boolean;
  onClick: () => void;
  onNameChangeClick: () => void;
  onDeleteClick: () => void;
  onDownloadClick: () => void;
  onSetContractClick: () => void;
}

function FileListItem({
  isContract,
  name,
  extension,
  hasUpdatePermission,
  onClick,
  onDeleteClick,
  onDownloadClick,
  onNameChangeClick,
  onSetContractClick,
}: FileListItemProps) {
  const [isContextMenuOpen, setIsContextMenuOpen] = useToggle(false);
  const closeContextMenuDecorator = (fn: () => void) => () => {
    fn();
    setIsContextMenuOpen(false);
  };

  const fileNameWithExtension = `${name}.${extension}`;

  return (
    <Flex
      alignItems="center"
      as="li"
      border="1px solid"
      borderColor="boxBorderNeutralLight"
      borderRadius="4px"
      pl={6}
      py={4}
    >
      <Tooltip content={fileNameWithExtension} placement="top">
        <Text
          as="button"
          ellipsis
          sx={{
            width: '100%',
            '&:hover': {
              textDecoration: 'underline',
              textUnderlineOffset: '0.2em',
            },
          }}
          textAlign="left"
          type="button"
          onClick={onClick}
        >
          {fileNameWithExtension}
        </Text>
      </Tooltip>
      <Box flex="auto" />
      {isContract && (
        <Text
          bg="statusBackgroundPositiveUltraLight"
          color="textPositiveBase"
          sx={{
            borderRadius: 3,
            flex: 'none',
            py: '2px',
            px: 2,
            ml: 3,
            mr: hasUpdatePermission ? 0 : 5,
          }}
        >
          계약서
        </Text>
      )}
      {hasUpdatePermission && (
        <Box flex="none" mx={1}>
          <ContextMenu isOpen={isContextMenuOpen} onOpenChange={setIsContextMenuOpen}>
            <ContextMenuTrigger>
              <SquareButton
                sx={{ border: 'none', boxShadow: 'none', height: '30px' }}
                onClick={setIsContextMenuOpen}
              >
                <ThreeDotsIcon />
              </SquareButton>
            </ContextMenuTrigger>
            <ContextMenuList left="0" top="34px" width="160px">
              {!isContract && (
                <ContextMenuItem
                  display="flex"
                  sx={{ alignItems: 'center' }}
                  onClick={closeContextMenuDecorator(onSetContractClick)}
                >
                  <Box color="textNeutralLight" mr={2} width="12px">
                    <FontAwesomeIcon icon={faCheck} />
                  </Box>
                  계약서로 설정
                </ContextMenuItem>
              )}
              <ContextMenuItem
                display="flex"
                sx={{ alignItems: 'center' }}
                onClick={closeContextMenuDecorator(onNameChangeClick)}
              >
                <Box color="textNeutralLight" mr={2} width="12px">
                  <FontAwesomeIcon icon={faEdit} />
                </Box>
                이름 변경
              </ContextMenuItem>
              <ContextMenuItem
                display="flex"
                sx={{ alignItems: 'center' }}
                onClick={closeContextMenuDecorator(onDownloadClick)}
              >
                <Box color="textNeutralLight" mr={2} width="12px">
                  <FontAwesomeIcon icon={faArrowDownToBracket} />
                </Box>
                다운로드
              </ContextMenuItem>
              {!isContract && (
                <>
                  <Box
                    as="li"
                    backgroundColor="dividerNeutralLight"
                    height="1px"
                    my={2}
                    role="separator"
                    width="100%"
                  />
                  <ContextMenuItem
                    display="flex"
                    sx={{ alignItems: 'center' }}
                    onClick={closeContextMenuDecorator(onDeleteClick)}
                  >
                    <Box color="textNeutralLight" mr={2} width="12px">
                      <FontAwesomeIcon icon={faTrashCan} />
                    </Box>
                    파일 삭제
                  </ContextMenuItem>
                </>
              )}
            </ContextMenuList>
          </ContextMenu>
        </Box>
      )}
    </Flex>
  );
}

interface NameChangeModalProps {
  isLoading: boolean;
  isOpen: boolean;
  name: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}

function NameChangeModal({ isLoading, isOpen, name, onCancel, onConfirm }: NameChangeModalProps) {
  const { register, handleSubmit, formState, setFocus } = useForm<{ name: string }>({
    mode: 'onChange',
  });

  useEffect(() => setFocus('name', { shouldSelect: true }), [setFocus]);

  return (
    <Modal isOpen={isOpen} maxWidth="xs" onClose={onCancel}>
      <ModalTitle as="h1">파일 이름 변경</ModalTitle>
      <Box
        as="form"
        width="100%"
        onSubmit={handleSubmit(({ name }) => {
          onConfirm(name);
        })}
      >
        <ModalContent>
          <Text fontWeight="bold">
            이름
            <Text color="textNegativeBase" fontWeight="bold">
              *
            </Text>
          </Text>
          <Input
            autoFocus
            defaultValue={name}
            isInvalid={!formState.isValid}
            mt={3}
            {...register('name', { required: true })}
            required
          />
        </ModalContent>
        <ModalFooter>
          <Button disabled={isLoading} type="button" onClick={onCancel}>
            취소
          </Button>
          <Button
            disabled={!(formState.isValid && formState.isDirty)}
            isLoading={isLoading}
            ml={3}
            type="submit"
            variant="primary"
          >
            변경하기
          </Button>
        </ModalFooter>
      </Box>
    </Modal>
  );
}
